/**
 * The bar that says "you are not you".
 *
 * An impersonated session looks exactly like an ordinary one — same chrome,
 * same data, the customer's name in the corner — which is precisely the danger.
 * The operator has to be unable to forget, so this is deliberately loud,
 * persistent, and at the very top of the shell above everything else.
 *
 * It renders nothing at all for an ordinary session, so mounting it is free.
 *
 * Nothing here is a security control. Read-only is enforced twice server-side —
 * reduced at mint time by the post-login Action and again per request by the
 * authorizer — and the expiry is enforced by the authorizer too. The banner's
 * job is to make an enforced boundary legible, so a refused write reads as "of
 * course, I'm read-only" rather than as a bug.
 */
import * as React from "react";
import { ShieldAlert } from "lucide-react";
import { getImpersonationState } from "@authe/fe-commons/auth";
import type { AutheClaims } from "@authe/fe-commons/auth";

export interface ImpersonationBannerProps {
  claims: AutheClaims | null | undefined;
  /** The token's top-level `sub` — the user being impersonated. */
  targetSub?: string | null;
  /** Clock injection for tests. */
  now?: number;
}

/** Minutes until `iso`, or null when there is no expiry / it has passed. */
function minutesLeft(iso: string | null, now: number): number | null {
  if (!iso) return null;
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return null;
  const mins = Math.ceil((ts - now) / 60_000);
  return mins > 0 ? mins : null;
}

export function ImpersonationBanner({
  claims,
  targetSub,
  now = Date.now(),
}: ImpersonationBannerProps): React.ReactElement | null {
  const state = getImpersonationState(claims, { targetSub, now });
  if (!state.active) return null;

  const remaining = minutesLeft(state.expiresAt, now);

  return (
    <div
      role="status"
      aria-live="polite"
      className={[
        "sticky top-0 z-50 flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2",
        "text-sm font-medium",
        state.expired
          ? "bg-destructive text-destructive-foreground"
          : "bg-amber-500 text-black dark:text-black",
      ].join(" ")}
    >
      <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />

      {state.expired ? (
        <span>
          This impersonation session has <strong>expired</strong>. Requests are being
          refused — close this tab and start a new session.
        </span>
      ) : (
        <>
          <span>
            You are viewing this account as{" "}
            <strong>{state.targetSub ?? "another user"}</strong>
            {state.accessLevel === "read_only" ? " (read-only)" : " (read/write)"}.
          </span>
          {remaining !== null ? (
            <span className="opacity-80">Ends in {remaining} min.</span>
          ) : null}
        </>
      )}

      {state.reason ? <span className="opacity-80">Reason: {state.reason}</span> : null}
      {state.sessionId ? (
        <span className="ml-auto font-mono text-xs opacity-70">{state.sessionId}</span>
      ) : null}
    </div>
  );
}
